import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	Location,
	ProfileDocumentKind,
	UserDocumentPayload,
	UserDocumentResponse,
} from '@/reservation/reservation.model';
import { LocationKinds } from '@/common/enums/LocationKinds';
import { ReservationSummaryApiService } from '../services/reservation-summary-api.service';

function emptyDocumentItem(): UserDocumentPayload {
	return { kind: 0, number: '', expirationDate: null, countryOfIssue: null };
}

type UserDocumentFormGroup = FormGroup<{
	kind: FormControl<number>;
	number: FormControl<string>;
	expirationDate: FormControl<Date | null>;
	countryOfIssue: FormControl<number | null>;
}>;

@Component({
	selector: 'app-reservation-summary-user-documents',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatFormFieldModule,
		MatSelectModule,
		MatInputModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatButtonModule,
		MatTooltipModule,
	],
	templateUrl: './reservation-summary-user-documents.component.html',
	styleUrls: ['./reservation-summary-user-documents.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReservationSummaryUserDocumentsComponent {
	readonly initialUserDocuments = input<UserDocumentResponse[] | undefined>();
	readonly documentsChange = output<UserDocumentPayload[]>();

	readonly documentsForm = new FormArray<UserDocumentFormGroup>([]);
	readonly form = new FormGroup({
		documents: this.documentsForm,
	});

	readonly loadedData = toSignal(
		(() => {
			const api = inject(ReservationSummaryApiService);
			return forkJoin({
				documentKinds: api.getDocumentKinds(),
				locations: api.getLocations(),
			}).pipe(
				map(({ documentKinds, locations }) => ({
					documentKinds,
					countries: locations
						.filter((loc) => loc.locationKind === LocationKinds.COUNTRY)
						.sort((a, b) => {
							const aIsPoland = a.name.toLocaleLowerCase('pl-PL') === 'polska';
							const bIsPoland = b.name.toLocaleLowerCase('pl-PL') === 'polska';
							if (aIsPoland && !bIsPoland) {
								return -1;
							}
							if (!aIsPoland && bIsPoland) {
								return 1;
							}
							return a.name.localeCompare(b.name, 'pl-PL', { sensitivity: 'base' });
						}),
				})),
			);
		})(),
		{
			initialValue: {
				documentKinds: [] as ProfileDocumentKind[],
				countries: [] as Location[],
			},
		},
	);

	readonly documentKinds = computed(() => this.loadedData().documentKinds);
	readonly countries = computed(() => this.loadedData().countries);
	readonly documents = toSignal(this.documentsForm.valueChanges, {
		initialValue: this.documentsForm.getRawValue(),
	});

	readonly isAddDocumentDisabled = computed(
		() => this.documents().length >= this.documentKinds().length && this.documentKinds().length > 0,
	);

	readonly documentsPayload = computed(() =>
		this.documents().map((d) => ({
			...d,
			expirationDate: d.expirationDate ? (this.datePipe.transform(d.expirationDate, 'yyyy-MM-dd') ?? '') : '',
		})),
	);

	private readonly datePipe = inject(DatePipe);

	constructor() {
		effect(
			() => {
				const docs = this.initialUserDocuments();
				const kinds = this.documentKinds();
				const countriesList = this.countries();
				if (docs?.length && kinds.length && countriesList.length) {
					this.setDocuments(
						docs.map((doc) => ({
							kind: doc.kind ?? 0,
							number: doc.number ?? '',
							expirationDate: doc.expirationDate ? new Date(doc.expirationDate) : null,
							countryOfIssue: doc.countryOfIssue?.id ?? null,
						})),
					);
				}
			},
			{ allowSignalWrites: true },
		);

		effect(
			() => {
				this.documentsChange.emit(this.documentsPayload());
			},
			{ allowSignalWrites: true },
		);
	}

	addDocument(): void {
		this.documentsForm.push(this.createDocumentForm(emptyDocumentItem()));
	}

	removeDocument(index: number): void {
		this.documentsForm.removeAt(index);
	}

	private setDocuments(documents: UserDocumentPayload[]): void {
		this.documentsForm.clear();
		documents.forEach((document) => {
			this.documentsForm.push(this.createDocumentForm(document));
		});
	}

	private createDocumentForm(document: UserDocumentPayload): UserDocumentFormGroup {
		return new FormGroup({
			kind: new FormControl(document.kind, { nonNullable: true }),
			number: new FormControl(document.number, { nonNullable: true }),
			expirationDate: new FormControl(document.expirationDate ? new Date(document.expirationDate) : null),
			countryOfIssue: new FormControl(document.countryOfIssue ?? null),
		});
	}
}
